<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@stores/appearance'
import { usePremiumStore } from '@stores/premium'
import { useMessages } from '@composables/use-messages'
import DataLayer from '@services/data-layer/index'
import SubscriptionHeader from './partials/subscription-header.vue'

const appearanceStore = useAppearanceStore()
const premiumStore = usePremiumStore()
const router = useRouter()
const getMessageText = useMessages()

const isStripePortalLoading = ref(false)

const currentSubscription = computed(() => premiumStore.currentSubscription)
const paymentMethod = computed(() => premiumStore.paymentMethod)
const isLoading = computed(() => premiumStore.isLoading)

const showManagementSection = computed(
    () =>
        (paymentMethod.value === 'stripe' || paymentMethod.value === 'paypal') &&
        currentSubscription.value.plan.active === 1
)
const showViewPlansSection = computed(() => currentSubscription.value.plan.price === 0)
const showCardSection = computed(
    () => paymentMethod.value === 'stripe' && currentSubscription.value.plan.active === 1
)
const showSubscriptionSection = computed(
    () => showManagementSection.value || showViewPlansSection.value || showCardSection.value
)

const goToStripePortal = () => {
    isStripePortalLoading.value = true
    DataLayer.subscriptions
        .getStripePortalUrl()
        .then(response => {
            window.location.href = response.body.portalUrl
            isStripePortalLoading.value = false
        })
        .catch(() => {
            appearanceStore.handleNotification({
                message: getMessageText('SOMETHING_WENT_WRONG'),
                type: 'danger',
            })
            isStripePortalLoading.value = false
        })
}

const handlePremiumManagement = () => {
    if (showCardSection.value) goToStripePortal()
    else router.push('/account/manage-subscription')
}

usePageHead({ title: 'Account - Overview', description: 'Account - Overview' })
</script>

<template>
    <div>
        <div v-if="isLoading">
            <div class="tiles">
                <BaseCard class="skeleton loader"/>
                <BaseCard class="skeleton loader"/>
                <BaseCard class="skeleton loader"/>
                <BaseCard class="skeleton loader"/>
            </div>
        </div>
        <div
            v-else
            class="tiles"
        >
            <SubscriptionHeader
                :is-stripe-portal-loading="isStripePortalLoading"
                :allow-update-card="showCardSection"
                @manage-premium="handlePremiumManagement"
            />

            <BaseCard class="card-section">
                <template #header>
                    <div>
                        <h3 class="card-header">Your Account</h3>
                        <div class="card-links">
                            <BaseCardLink
                                text="Edit Profile"
                                to="/account/edit-profile"
                                icon="user"
                            />
                        </div>
                    </div>
                </template>
            </BaseCard>
            <BaseCard
                v-if="showSubscriptionSection"
                class="card-section"
            >
                <template #header>
                    <div>
                        <h3 class="card-header">Subscription</h3>
                        <BaseCardLink
                            v-if="showManagementSection"
                            text="Manage subscription"
                            icon="logo-frisky"
                            :is-loading="isStripePortalLoading"
                            @click="handlePremiumManagement"
                        />
                        <BaseCardLink
                            v-if="showViewPlansSection"
                            text="Available plans"
                            to="/premium/view-plans"
                            icon="cassette"
                        />
                        <BaseCardLink
                            v-if="showCardSection"
                            class="update-card-link"
                            text="Update card"
                            icon="wallet"
                            :is-loading="isStripePortalLoading"
                            @click="handlePremiumManagement"
                        />
                    </div>
                </template>
            </BaseCard>
            <BaseCard class="card-section">
                <template #header>
                    <div>
                        <h3 class="card-header">Security</h3>
                        <div class="card-links">
                            <BaseCardLink
                                text="Change password"
                                to="/account/change-password"
                                icon="lock"
                            />
                        </div>
                    </div>
                </template>
            </BaseCard>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.tiles {
    display: flex;
    flex-direction: column;
    gap: $s-l;

    .card-section {
        padding-bottom: $s-xxxl;
    }
}

.card-header {
    @extend %h5;

    color: $c-text-faded;
}

.loader {
    min-height: 200px;
}

@include tablet {
    .tiles {
        gap: $s-xxl;
    }
}
</style>
